import { useEffect, useState } from 'react';

const IMAGES_CACHE_KEY = 'astrobinImagesCache:v2';
const IMAGES_CACHE_TTL_MS = 1000 * 60 * 30;

const getCachedImages = () => {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const cachedValue = localStorage.getItem(IMAGES_CACHE_KEY);

        if (!cachedValue) {
            return [];
        }

        const parsedCache = JSON.parse(cachedValue);
        const cachedImages = Array.isArray(parsedCache?.images) ? parsedCache.images : [];
        const cachedAt = Number(parsedCache?.cachedAt);

        if (!cachedImages.length || !cachedAt || (Date.now() - cachedAt) > IMAGES_CACHE_TTL_MS) {
            localStorage.removeItem(IMAGES_CACHE_KEY);
            return [];
        }

        return cachedImages;
    } catch (error) {
        return [];
    }
};

const cacheImages = (images) => {
    if (typeof window === 'undefined' || !Array.isArray(images) || !images.length) {
        return;
    }

    try {
        localStorage.setItem(IMAGES_CACHE_KEY, JSON.stringify({
            images,
            cachedAt: Date.now()
        }));
    } catch (error) {
        // Ignore storage quota issues and keep the network result in memory.
    }
};

const useImagesData = (apiUrl) => {
    const [images, setImages] = useState(() => {
        const cachedImages = getCachedImages();
        return cachedImages;
    });
    const [imagesLoading, setImagesLoading] = useState(() => {
        const cachedImages = getCachedImages();
        return cachedImages.length === 0;
    });
    const [imagesError, setImagesError] = useState('');

    useEffect(() => {
        const getImages = async () => {
            try {
                const res = await fetch(`${apiUrl}/api/getImages`);
                if (!res.ok) {
                    throw new Error(`Request failed with status ${res.status}`);
                }

                const data = await res.json();
                const resolvedImages = Array.isArray(data) ? data : [];

                setImages(resolvedImages);
                cacheImages(resolvedImages);
                setImagesError('');
            } catch (err) {
                console.error('Failed to fetch images:', err);
                setImagesError('Unable to load the AstroBin gallery right now. Please try again in a moment.');
            } finally {
                setImagesLoading(false);
            }
        };

        getImages();
    }, [apiUrl]);

    return {
        images,
        imagesLoading,
        imagesError
    };
};

export default useImagesData;
